/**
 * P5 W4 — 共享 axios 客户端。
 *
 * 职责：
 * - baseURL = VITE_API_BASE_URL + '/api'
 *   - production: VITE_API_BASE_URL=https://cue-beauty-api.cueniverse.co.kr (构建时注入)
 *   - dev: VITE_API_BASE_URL='' (空) → baseURL='/api' → vite proxy 转 localhost:8089
 * - request interceptor 注入 Authorization: Bearer {access_token}
 * - response interceptor 401 → 单次尝试 refresh，失败则 kick logout
 * - withCredentials=true 让 refresh cookie 跨源发送
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios"

const _apiOrigin = import.meta.env.VITE_API_BASE_URL || ""

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${_apiOrigin}/api`,
  withCredentials: true,
  timeout: 30_000,
})

// ════════════════════════════════════════
// Token 注入（避免循环 import — store 用 setter 注入）
// ════════════════════════════════════════

let _getAccessToken: () => string | null = () => null
let _onAuthFailure: () => void = () => {}
let _onTokenRefresh: (newToken: string) => void = () => {}

export function configureApiClient(opts: {
  getAccessToken: () => string | null
  onAuthFailure: () => void
  onTokenRefresh: (newToken: string) => void
}) {
  _getAccessToken = opts.getAccessToken
  _onAuthFailure = opts.onAuthFailure
  _onTokenRefresh = opts.onTokenRefresh
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = _getAccessToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 401 自动 refresh（每个 client 至多一次防循环）
let _refreshPromise: Promise<string | null> | null = null

async function _doRefresh(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = (async () => {
    try {
      // 不走 apiClient 避免拦截器递归
      const resp = await axios.post(
        `${_apiOrigin}/api/staff/auth/refresh`,
        {},
        { withCredentials: true, timeout: 10_000 },
      )
      const newToken = resp?.data?.access_token as string | undefined
      if (newToken) {
        _onTokenRefresh(newToken)
        return newToken
      }
      return null
    } catch {
      return null
    } finally {
      // 100ms 后清，避免短时间多个 401 都触发 refresh
      setTimeout(() => {
        _refreshPromise = null
      }, 100)
    }
  })()
  return _refreshPromise
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const status = error.response?.status
    const original = error.config as
      | (AxiosRequestConfig & { _retried?: boolean })
      | undefined

    if (status === 401 && original && !original._retried) {
      original._retried = true
      // 不对 /staff/auth/login 和 /staff/auth/refresh 本身做重试
      const isAuthEndpoint =
        original.url?.includes("/staff/auth/login") ||
        original.url?.includes("/staff/auth/refresh")
      if (!isAuthEndpoint) {
        const newToken = await _doRefresh()
        if (newToken) {
          original.headers = original.headers || {}
          original.headers.Authorization = `Bearer ${newToken}`
          return apiClient.request(original)
        }
      }
      // refresh 失败 → kick out
      _onAuthFailure()
    }
    return Promise.reject(error)
  },
)
